import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import PageLayout from "../components/layout/PageLayout";
import { LoadingOverlay } from "../components/ui/loading-overlay";
import { fetchWithAuth } from "../Context/UserContext";
import API_URL from "../config";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";

const StatTile = ({ label, value }) => (
  <Card>
    <CardContent className="pt-6">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
    </CardContent>
  </Card>
);

const RANGOS = [
  { value: 7, label: "7 días" },
  { value: 30, label: "30 días" },
  { value: 90, label: "90 días" },
];

const BarRow = ({ label, value, max }) => (
  <div className="flex items-center gap-3 py-1.5 text-sm">
    <span className="w-40 shrink-0 truncate text-foreground" title={label}>
      {label}
    </span>
    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full bg-primary"
        style={{ width: max > 0 ? `${Math.max((value / max) * 100, 3)}%` : "0%" }}
      />
    </div>
    <span className="w-10 shrink-0 text-right text-muted-foreground">{value}</span>
  </div>
);

const StaffVisitas = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [dias, setDias] = useState(30);

  useEffect(() => {
    const fetchVisitas = async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(`${API_URL}/bs/staff/visitas?dias=${dias}`);
        if (response.ok) {
          setData(await response.json());
        } else {
          const body = await response.json();
          setError(body.error || "No tienes acceso a esta sección.");
        }
      } catch (err) {
        console.error("Error al cargar las visitas:", err);
        setError("Error en la conexión con el servidor.");
      } finally {
        setLoading(false);
      }
    };
    fetchVisitas();
  }, [dias]);

  if (loading && !data) {
    return <LoadingOverlay />;
  }

  const maxPagina = data ? Math.max(0, ...data.top_paginas.map((p) => p.visitas)) : 0;
  const maxDia = data ? Math.max(0, ...data.visitas_por_dia.map((d) => d.visitas)) : 0;
  const maxReferrer = data ? Math.max(0, ...data.top_referrers.map((r) => r.visitas)) : 0;

  return (
    <PageLayout>
      <Helmet>
        <title>Visitas del sitio - Procu</title>
      </Helmet>

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Visitas del sitio</h1>
            <p className="text-sm text-muted-foreground">
              Tráfico registrado por el propio sitio (no depende de Google Analytics). Solo visible para el equipo de Procu.
            </p>
          </div>
          <div className="flex gap-2">
            {RANGOS.map((rango) => (
              <Button
                key={rango.value}
                variant={dias === rango.value ? "default" : "outline"}
                size="sm"
                onClick={() => setDias(rango.value)}
              >
                {rango.label}
              </Button>
            ))}
          </div>
        </div>

        {error && <p className="mt-6 text-sm text-destructive">{error}</p>}

        {data && (
          <>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatTile label="Visitas totales" value={data.total_visitas} />
              <StatTile label="Visitantes únicos" value={data.visitantes_unicos} />
              <StatTile label="Usuarios logueados únicos" value={data.usuarios_logueados_unicos} />
            </div>

            <h2 className="mt-8 text-sm font-semibold text-foreground">Visitas por día</h2>
            <Card className="mt-3">
              <CardContent className="pt-6">
                {data.visitas_por_dia.length === 0 && (
                  <p className="text-sm text-muted-foreground">Sin datos en este rango.</p>
                )}
                {data.visitas_por_dia.map((dia) => (
                  <BarRow key={dia.fecha} label={dia.fecha} value={dia.visitas} max={maxDia} />
                ))}
              </CardContent>
            </Card>

            <h2 className="mt-8 text-sm font-semibold text-foreground">Páginas más visitadas</h2>
            <Card className="mt-3">
              <CardContent className="pt-6">
                {data.top_paginas.length === 0 && (
                  <p className="text-sm text-muted-foreground">Sin datos en este rango.</p>
                )}
                {data.top_paginas.map((pagina) => (
                  <BarRow key={pagina.path} label={pagina.path} value={pagina.visitas} max={maxPagina} />
                ))}
              </CardContent>
            </Card>

            <h2 className="mt-8 text-sm font-semibold text-foreground">De dónde llegan</h2>
            <Card className="mt-3">
              <CardContent className="pt-6">
                {data.top_referrers.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Sin referrers registrados (mayoría de visitas directas o desde la propia app).
                  </p>
                )}
                {data.top_referrers.map((ref) => (
                  <BarRow key={ref.referrer} label={ref.referrer} value={ref.visitas} max={maxReferrer} />
                ))}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default StaffVisitas;
